import { AuthProvider } from '@refinedev/core';
import { supabase } from './lib/supabase';
import { logger } from './utils/logger';

export const authProvider: AuthProvider = {
	login: async ({ email, password, rememberMe }) => {
		if (import.meta.env.DEV) {
			logger.debug('Auth login attempt', {
				email,
				passwordLength: password?.length || 0,
				hasSupabaseUrl: Boolean(import.meta.env.VITE_SUPABASE_URL),
				hasAnonKey: Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY),
			});
		}

		// Guard against empty credentials to avoid unnecessary network requests
		if (!email || !password) {
			return {
				success: false,
				error: {
					message: 'Email and password are required',
					name: 'Validation Error',
				},
			};
		}
		try {
			if (import.meta.env.DEV) {
				logger.debug('Executing Supabase auth request');
			}
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			if (import.meta.env.DEV) {
				logger.debug('Auth response received', {
					hasData: Boolean(data),
					error: error?.message,
				});
			}

			if (error) {
				// Enhanced error handling with specific error types
				let errorMessage = error.message;

				if (error.message.includes('Invalid login credentials')) {
					errorMessage =
						'Invalid email or password. Please check your credentials and try again.';
				} else if (error.message.includes('Email not confirmed')) {
					errorMessage =
						'Please check your email and click the confirmation link before signing in.';
				} else if (error.message.includes('Too many requests')) {
					errorMessage =
						'Too many login attempts. Please wait a few minutes before trying again.';
				}

				return {
					success: false,
					error: {
						message: errorMessage,
						name: 'Login Error',
					},
				};
			}

			if (data?.user) {
				// Store user preferences for remember me functionality
				if (rememberMe) {
					try {
						await supabase.from('user_preferences').upsert(
							{
								user_id: data.user.id,
								remember_me: true,
								updated_at: new Date().toISOString(),
							},
							{
								onConflict: 'user_id',
							}
						);
					} catch (_prefError) {
						// Don't fail login if preferences can't be saved
						// Silently handle preference storage errors
					}
				}

				return {
					success: true,
					redirectTo: '/admin',
				};
			}

			return {
				success: false,
				error: {
					message: 'Login failed - no user data received',
					name: 'Login Error',
				},
			};
		} catch (error: unknown) {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred during login';
			logger.error('Login request failed', error);
			return {
				success: false,
				error: {
					message: errorMessage,
					name: 'Login Error',
				},
			};
		}
	},

	logout: async () => {
		try {
			const { data: userData } = await supabase.auth.getUser();

			const { error } = await supabase.auth.signOut();

			if (error) {
				return {
					success: false,
					error: {
						message: error.message,
						name: 'Logout Error',
					},
				};
			}

			// Clear user preferences on logout
			if (userData?.user) {
				try {
					await supabase
						.from('user_preferences')
						.delete()
						.eq('user_id', userData.user.id);
				} catch (_prefError) {
					// Don't fail logout if preferences can't be cleared
					// Silently handle preference cleanup errors
				}
			}

			return {
				success: true,
				redirectTo: '/admin/login',
			};
		} catch (error: unknown) {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred during logout';
			logger.error('Logout request failed', error);
			return {
				success: false,
				error: {
					message: errorMessage,
					name: 'Logout Error',
				},
			};
		}
	},

	check: async () => {
		try {
			const { data, error } = await supabase.auth.getSession();

			if (error) {
				return {
					authenticated: false,
					redirectTo: '/admin/login',
					error: {
						message: 'Session validation failed',
						name: 'Session Error',
					},
				};
			}

			const { session } = data;

			if (session) {
				// Additional validation: check if token is close to expiry
				const expiresAt = session.expires_at;
				const now = Math.floor(Date.now() / 1000);
				const timeUntilExpiry = expiresAt ? expiresAt - now : 0;

				// If token expires in less than 5 minutes, try to refresh
				if (timeUntilExpiry < 300 && timeUntilExpiry > 0) {
					try {
						const { error: refreshError } =
							await supabase.auth.refreshSession();
						if (refreshError) {
							return {
								authenticated: false,
								redirectTo: '/admin/login',
								error: {
									message: 'Session expired and could not be refreshed',
									name: 'Session Error',
								},
							};
						}
			} catch (_refreshError) {
						return {
							authenticated: false,
							redirectTo: '/admin/login',
						};
					}
				}

				return {
					authenticated: true,
				};
			}

			return {
				authenticated: false,
				redirectTo: '/admin/login',
			};
		} catch (_error: unknown) {
			return {
				authenticated: false,
				redirectTo: '/admin/login',
				error: {
					message: 'Session validation failed',
					name: 'Session Error',
				},
			};
		}
	},

	getPermissions: async () => {
		try {
			const { data, error } = await supabase.auth.getUser();

			if (error) {
				return null;
			}

			if (data?.user) {
				// Return user role or default to 'admin' for authenticated users
				return data.user.role || 'admin';
			}

			return null;
		} catch (_error) {
			return null;
		}
	},

	getIdentity: async () => {
		try {
			const { data, error } = await supabase.auth.getUser();

			if (error) {
				return null;
			}

			if (data?.user) {
				// Get user preferences if available
				let rememberMe = false;
				try {
					const { data: preferences } = await supabase
						.from('user_preferences')
						.select('remember_me')
						.eq('user_id', data.user.id)
						.single();

					rememberMe = preferences?.remember_me || false;
				} catch {
					// Ignore preferences errors
				}

				return {
					id: data.user.id,
					name: data.user.email,
					email: data.user.email,
					avatar: data.user.user_metadata?.avatar_url,
					rememberMe,
				};
			}

			return null;
		} catch (_error) {
			return null;
		}
	},

	onError: async (error) => {
		// Handle specific error cases
		if (error?.status === 401 || error?.message?.includes('JWT')) {
			return {
				logout: true,
			};
		}

		if (error?.status === 403) {
			return {
				error: {
					message:
						'Access denied. You do not have permission to perform this action.',
					name: 'Permission Error',
				},
			};
		}

		if (error?.status === 429) {
			return {
				error: {
					message:
						'Too many requests. Please wait a moment before trying again.',
					name: 'Rate Limit Error',
				},
			};
		}

		// Network errors
		if (
			error?.message?.includes('fetch') ||
			error?.message?.includes('network')
		) {
			return {
				error: {
					message: 'Network error. Please check your connection and try again.',
					name: 'Network Error',
				},
			};
		}

		return {
			error: {
				message: error?.message || 'An unexpected error occurred',
				name: 'Unknown Error',
			},
		};
	},
};
